/**
 * Boot-time diagnostic for voice features. Logs a single structured
 * line on each call connect with everything support / dev need to
 * triage cross-browser audio bugs (Safari/Firefox/Edge/Opera).
 *
 * Why: when a user reports "no noise suppression in <browser>" the
 * first question is always "what features does their browser
 * actually have?" Without this, every report turns into a back-
 * and-forth asking the user to paste navigator.userAgent + check
 * AudioWorklet support + verify WASM SIMD — a 10-message thread.
 * With this, the user shares one console line and we see everything.
 *
 * Runs at most once per page-load (cached via `_diagnosed`) so the
 * log doesn't repeat on every mic re-publish. The cache is per page,
 * not persisted — a refresh gets a fresh diagnostic, which is the
 * right behaviour when capabilities might have changed (browser
 * update, settings change between loads).
 *
 * The computed snapshot is also reused by the temporary voice-diag
 * telemetry collector (voiceTelemetry.ts) via `getVoiceDiagnosticData()`
 * — a pure read of capabilities with NO side effects on audio state.
 */

let _diagnosed = false

export interface VoiceDiagnosticData {
  browser: { name: string; version: string }
  platform: string
  lang: string
  hardwareConcurrency: number | null
  ua: string
  audio: {
    hasAudioContext: boolean
    hasAudioWorklet: boolean
    hasMediaStreamSource: boolean
    hasMediaStreamDest: boolean
    hasGetUserMedia: boolean
    requested48kHz_got: number | null
    ctxStateAtCreate: AudioContextState | null
  }
  wasm: { hasSimd: boolean; hasModuleWorker: boolean }
}

let _cached: VoiceDiagnosticData | null = null

/**
 * Compute (once, memoized) the voice-capability snapshot. Pure read of
 * browser features. The 48 kHz probe opens + immediately closes a throw-
 * away AudioContext; it does not touch the call's audio graph.
 */
export function getVoiceDiagnosticData(): VoiceDiagnosticData {
  if (_cached) return _cached

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const platform = typeof navigator !== 'undefined' ? navigator.platform : ''
  const lang = typeof navigator !== 'undefined' ? navigator.language : ''
  const hardware = typeof navigator !== 'undefined' ? (navigator as any).hardwareConcurrency : null

  const browser = detectBrowser(ua)
  const audioCtxClass = typeof window !== 'undefined'
    ? (window.AudioContext || (window as any).webkitAudioContext)
    : null
  const hasAudioWorklet = !!(audioCtxClass && audioCtxClass.prototype && 'audioWorklet' in audioCtxClass.prototype)
  const hasModuleWorker = (() => {
    if (typeof Worker === 'undefined') return false
    try {
      // Smoke-test by constructing a tiny inline module worker. Throws on
      // browsers that don't support `{ type: 'module' }` (Safari < 15,
      // Firefox < 114, old Chromium).
      const w = new Worker('data:application/javascript,', { type: 'module' })
      w.terminate()
      return true
    } catch {
      return false
    }
  })()
  const hasWasmSimd = (() => {
    if (typeof WebAssembly === 'undefined') return false
    try {
      // Smallest known WASM module that uses an i8x16.splat instruction
      // — invalid on browsers without the SIMD proposal.
      return WebAssembly.validate(new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0,
        1, 5, 1, 96, 0, 1, 123,
        3, 2, 1, 0,
        10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
      ]))
    } catch {
      return false
    }
  })()
  const hasMediaStreamSource = !!(audioCtxClass && audioCtxClass.prototype && 'createMediaStreamSource' in audioCtxClass.prototype)
  const hasMediaStreamDest = !!(audioCtxClass && audioCtxClass.prototype && 'createMediaStreamDestination' in audioCtxClass.prototype)
  const hasGetUserMedia = !!(
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia
  )

  let sampleRate: number | null = null
  let ctxState: AudioContextState | null = null
  if (audioCtxClass) {
    try {
      const probe = new audioCtxClass({ sampleRate: 48000 })
      sampleRate = probe.sampleRate
      ctxState = probe.state
      probe.close().catch(() => {})
    } catch {
      // Some browsers throw if they can't honour the rate hint
      try {
        const probe = new audioCtxClass()
        sampleRate = probe.sampleRate
        ctxState = probe.state
        probe.close().catch(() => {})
      } catch { /* total failure — leave nulls */ }
    }
  }

  _cached = {
    browser,
    platform,
    lang,
    hardwareConcurrency: hardware,
    ua,
    audio: {
      hasAudioContext: !!audioCtxClass,
      hasAudioWorklet,
      hasMediaStreamSource,
      hasMediaStreamDest,
      hasGetUserMedia,
      requested48kHz_got: sampleRate,
      ctxStateAtCreate: ctxState,
    },
    wasm: {
      hasSimd: hasWasmSimd,
      hasModuleWorker,
    },
  }
  return _cached
}

export function runVoiceDiagnostic(): void {
  if (_diagnosed) return
  _diagnosed = true

  const result = getVoiceDiagnosticData()

  // Single grep-friendly line so support can ctrl-F "voiceDiagnostic"
  // in a user's pasted console dump and see everything.
  console.info('[voiceDiagnostic]', JSON.stringify(result, null, 2))

  // Surface known-broken combos with explicit warnings — those are
  // the ones we should be cascading around but the user might not
  // realise why their selected backend isn't running.
  if (!result.audio.hasAudioWorklet) {
    console.warn('[voiceDiagnostic] AudioWorklet NOT supported — DFN + RNNoise both unavailable. ' +
      'Falling back to browser-native noise suppression only.')
  }
  if (!result.wasm.hasSimd) {
    console.warn('[voiceDiagnostic] WASM SIMD NOT supported — DFN inference will run on the scalar ' +
      'WASM path (much slower, likely to trigger our degradation auto-fallback to RNNoise).')
  }
  if (result.audio.requested48kHz_got !== null && result.audio.requested48kHz_got !== 48000) {
    console.warn(`[voiceDiagnostic] AudioContext returned sampleRate=${result.audio.requested48kHz_got} instead of 48000 ` +
      '— DFN cannot run at this rate, will cascade to RNNoise.')
  }
  if (!result.wasm.hasModuleWorker) {
    console.warn('[voiceDiagnostic] Module workers NOT supported (Safari < 15, Firefox < 114). ' +
      'DFN worker creation will fail; cascade falls to RNNoise.')
  }
}

function detectBrowser(ua: string): { name: string; version: string } {
  // Order matters: Edge contains "Chrome" and "Safari"; Opera contains
  // "Chrome"; Chrome contains "Safari". Match the most specific first.
  const m = (re: RegExp): string => {
    const r = ua.match(re)
    return r ? r[1] : ''
  }
  if (/Edg\//.test(ua))    return { name: 'Edge',    version: m(/Edg\/([\d.]+)/) }
  if (/OPR\//.test(ua))    return { name: 'Opera',   version: m(/OPR\/([\d.]+)/) }
  if (/Firefox\//.test(ua)) return { name: 'Firefox', version: m(/Firefox\/([\d.]+)/) }
  if (/Chrome\//.test(ua) && !/Edg\/|OPR\//.test(ua)) return { name: 'Chrome', version: m(/Chrome\/([\d.]+)/) }
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua))   return { name: 'Safari', version: m(/Version\/([\d.]+)/) }
  return { name: 'unknown', version: '' }
}
